import "bootstrap/dist/css/bootstrap.min.css";
import { useEffect, useMemo, useRef, useState } from "react";
import styled, { ThemeProvider } from "styled-components";
import { GlobalStyle } from "./styles/GlobalStyle";
import "./styles/main.scss";

import { apiFetch } from "./Components/ApiCall";
import CDRates from "./Components/CDRates";
import { NoAffiliateSection } from "./Components/CDRates/NoAffiliateSection";
import { setAccessToken } from "./auth";
import { configToTheme, DEFAULT_STYLES } from "./theme/APIConfigTheme";
import { trackEvent, trackPage } from "./utils/analytics";
import {
  AFFILIATE_AUTH,
  CDVALET_BASE_URL,
  TRACKDEV_URL,
} from "./utils/constant";
import { safeTrackPage, safeTrackEvent } from "./utils/analyticsHelpers";

const Shell = styled.div.attrs({ id: "cdv-widget" })`
  width: 100%;
  height: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;

const App = () => {
  const [styleConfig, setStyleConfig] = useState(DEFAULT_STYLES);
  const [loaded, setLoaded] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [depositAmount, setDepositAmount] = useState(0);
  const configRef = useRef(null);
  const lastHeightRef = useRef(document.body.scrollHeight);
  const defaultHeaders = { Origin: CDVALET_BASE_URL };

  const affiliateId = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get("affiliateId");
    } catch {
      return null;
    }
  }, []);

  const postIframeSize = (cfg) => {
    const { height, width, heightUnit, widthUnit } = cfg || {};
    let bodyHeight = document.body.scrollHeight + 20;
    if (bodyHeight < 200) bodyHeight = 300;
    let finalHeight;
    if (!height || height === "0") {
      finalHeight = "350px";
    } else {
      finalHeight =
        heightUnit === "%"
          ? height
            ? (bodyHeight * height) / 100 + "px"
            : document.body.scrollHeight + "px"
          : height + heightUnit;
    }
    const finalWidth = width && width !== "0" ? width + widthUnit : "100%";
    window.parent.postMessage(
      { type: "SET_IFRAME_SIZE", height: finalHeight, width: finalWidth },
      "*",
    );
  };

  const fetchToken = async () => {
    const url = `${AFFILIATE_AUTH}${affiliateId}`;
    // const url = `${AFFILIATE_AUTH}af-6f733bda-7445976888`;
    const resp = await apiFetch(url, "GET", null, defaultHeaders, TRACKDEV_URL);
    const tokenObj = resp?.data;
    if (!tokenObj || !tokenObj.token) {
      safeTrackEvent("Affiliate_page_loading_Action", {
        message: "No token returned from token endpoint",
        affiliateId,
      });
      throw new Error("No token returned from token endpoint");
    }
    localStorage.setItem("sessionId", tokenObj?.sessionId);
    setAccessToken(tokenObj, TRACKDEV_URL);
  };

  const fetchConfig = async () => {
    const resp = await apiFetch(
      `/api/v1/affiliate/${affiliateId}`,
      "GET",
      null,
      defaultHeaders,
      TRACKDEV_URL,
    );
    setDepositAmount(resp?.data?.depositAmount);
    return resp?.data ?? null;
  };

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      try {
        await fetchToken();
      } catch (err) {
        setIsActive(false);
        console.error("failed to get initial token:", err?.message ?? err);
      }
      try {
        const cfg = await fetchConfig();
        configRef.current = cfg;
        if (mounted && cfg) setStyleConfig(cfg);
        safeTrackEvent("Affiliate_page_loading_Action", {
          message: "Received configuration",
          affiliateId,
        });
      } catch (err) {
        console.error("failed to get config data:", err?.message ?? err);
        safeTrackEvent("Affiliate_page_loading_Action", {
          message: "Failed to get config data",
          affiliateId,
        });
      } finally {
        postIframeSize(configRef.current);
        safeTrackPage(window.location.href);
        if (mounted) setLoaded(true);
      }
    };

    init();

    const interval = setInterval(() => {
      const currentBodyHeight = document.body.scrollHeight;
      // Check if content height has actually changed
      if (currentBodyHeight !== lastHeightRef.current) {
        // Only run the lightweight height posting logic
        postIframeSize(configRef.current);
        lastHeightRef.current = currentBodyHeight;
      }
    }, 1000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [affiliateId]);

  const theme = useMemo(() => configToTheme(styleConfig), [styleConfig]);

  if (!loaded) return null;

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Shell>
        <a id="openNewTabAnchor" href="#" aria-hidden="true" />
        {isActive ? (
          <CDRates
            perPageRecord={theme.perPage}
            showPagination={theme?.flags?.showPagination}
            headerLogo={theme?.flags?.headerLogo}
            footerLogo={theme?.flags?.footerLogo}
            depositAmount={depositAmount}
          />
        ) : (
          <NoAffiliateSection />
        )}
      </Shell>
    </ThemeProvider>
  );
};

export default App;
